import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { memoryLLMService, MemorySummary } from '../services/memoryLLMService';
import { MarkdownContent } from './MarkdownContent';

interface EnhancedLoadingStatusProps {
  status: string;
  query?: string;
  intentType?: string;
  memorySummary?: MemorySummary | null;
}

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const swirl = keyframes`
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(0.8); }
  100% { transform: rotate(360deg) scale(1); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  padding: 40px;
  min-height: 60vh;
  animation: ${fadeIn} 0.5s ease-out;
`;

const RabbitHoleContainer = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Vortex = styled.div`
  position: absolute;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    transparent 30%,
    var(--color-primary)20 40%,
    var(--color-primary)40 60%,
    var(--color-primary)60 80%,
    transparent 90%
  );
  animation: ${swirl} 3s linear infinite;
`;

const InnerVortex = styled.div`
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    var(--color-secondary)40 0%,
    transparent 70%
  );
  animation: ${swirl} 2s linear infinite reverse;
`;

const Particle = styled.div<{ delay: number; angle: number }>`
  position: absolute;
  width: 4px;
  height: 4px;
  background: var(--color-accent);
  border-radius: 50%;
  animation: ${float} 2s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  transform: rotate(${props => props.angle}deg) translateX(80px);
  opacity: 0.8;
`;

const RabbitIcon = styled.div`
  font-size: 40px;
  animation: ${pulse} 2s ease-in-out infinite;
  z-index: 10;
`;

const StatusContainer = styled.div`
  text-align: center;
  max-width: 400px;
`;

const StatusText = styled.h3`
  font-family: var(--font-heading);
  font-size: var(--font-size-lg);
  color: var(--color-text);
  margin: 0 0 var(--gap-sm) 0;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-tight);
`;

const SubText = styled.p`
  font-family: var(--font-body);
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0 0 var(--gap-md) 0;
  line-height: 1.5;
  letter-spacing: var(--letter-spacing-normal);
`;

const FandomMessage = styled.div`
  font-family: var(--font-body);
  font-size: var(--font-size-sm);
  color: var(--color-primary);
  font-style: italic;
  animation: ${fadeIn} 1s ease-out;
  min-height: 20px;
  letter-spacing: var(--letter-spacing-wide);
`;

const MemoryContext = styled.div`
  font-family: var(--font-body);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-top: 12px;
  padding: 12px;
  background: var(--color-primary)10;
  border-radius: var(--radius-sm);
  animation: ${fadeIn} 1.5s ease-out;
`;

const MemorySummaryContainer = styled.div`
  margin-top: 30px;
  padding: 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  animation: ${fadeIn} 0.5s ease-out;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const MemoryTitle = styled.div`
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--color-text);
  font-size: 15px;
  opacity: 0.8;
`;

const MemorySummaryText = styled.p`
  line-height: 1.6;
  color: var(--color-text);
  margin-bottom: 16px;
  font-size: 14px;
`;

const MemoryInsight = styled.div`
  padding: 8px 0;
  color: var(--color-text);
  border-left: 2px solid var(--color-primary)30;
  padding-left: 12px;
  margin: 8px 0;
  font-size: 13px;
  line-height: 1.5;
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid var(--color-primary)20;
  border-top: 2px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 20px auto 0 auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;


// Fandom-specific loading messages
const fandomMessages: Record<string, string[]> = {
  THEORY: [
    "Consulting the ancient scrolls...",
    "Gathering evidence from across the multiverse...",
    "The conspiracy board is coming together...",
    "Connecting the red strings...",
    "Diving deep into the lore archives..."
  ],
  SHIP: [
    "Analyzing chemistry levels...",
    "Checking AO3 for compatibility...",
    "Measuring the slow burn potential...",
    "Calculating enemies-to-lovers probability...",
    "Reading between the lines..."
  ],
  MEME: [
    "Loading the freshest memes...",
    "Charging up the meme generators...",
    "Consulting the sacred texts of Reddit...",
    "Gathering the dankest content...",
    "Preparing maximum chaos..."
  ],
  CONTROVERSY: [
    "Grabbing popcorn for the drama...",
    "Sorting by controversial...",
    "Preparing the tea ☕...",
    "Loading hot takes...",
    "Diving into the discourse..."
  ],
  CANON: [
    "Accessing official archives...",
    "Verifying with the sacred canon...",
    "Consulting the wiki elders...",
    "Loading confirmed lore...",
    "Checking creator statements..."
  ],
  CHARACTER: [
    "Building character profiles...",
    "Analyzing character arcs...",
    "Loading backstory files...",
    "Gathering character development data...",
    "Compiling fan theories..."
  ]
};


const EnhancedLoadingStatus: React.FC<EnhancedLoadingStatusProps> = ({ status, query, intentType = 'GENERAL', memorySummary }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [memoryContext, setMemoryContext] = useState<string | null>(null);
  const messages = fandomMessages[intentType] || fandomMessages.THEORY;

  // Load memory context when we start analyzing
  useEffect(() => {
    if (status === 'analyzing' && query) {
      // Memory context is now handled by memorySummary prop
      setMemoryContext(null);
    }
  }, [status, query]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % messages.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [messages.length]);

  const getStatusMessage = (status: string): { main: string; sub: string } => {
    const messages: Record<string, { main: string; sub: string }> = {
      'initializing': { main: 'Opening the rabbit hole...', sub: 'Preparing your journey' },
      'analyzing': { main: 'Understanding your curiosity...', sub: 'Detecting fandom context' },
      'generating': { main: 'Crafting your exploration...', sub: 'Building comprehensive insights' },
      'extracting': { main: 'Finding hidden connections...', sub: 'Discovering related concepts' },
      'searching': { main: 'Scouring the fandom archives...', sub: 'Finding the best sources' },
      'organizing': { main: 'Mapping your journey...', sub: 'Creating your exploration path' },
      'finalizing': { main: 'Almost there...', sub: 'Preparing your rabbit hole' }
    };

    return messages[status] || { main: 'Processing...', sub: 'Please wait' };
  };

  const { main, sub } = getStatusMessage(status);

  // Compact view when memory is loaded
  if (memorySummary) {
    return (
      <Container>
        <MemorySummaryContainer>
          <MemoryTitle>Building on what you know...</MemoryTitle>
          <MarkdownContent content={memorySummary.summary} />
          
          {memorySummary.unexploredAngles && memorySummary.unexploredAngles.length > 0 && (
            <div>
              {memorySummary.unexploredAngles.slice(0, 2).map((angle, i) => (
                <MemoryInsight key={i}>
                  <MarkdownContent content={angle} />
                </MemoryInsight>
              ))}
            </div>
          )}
          
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <StatusText style={{ fontSize: 'var(--font-size-base)', marginBottom: '8px' }}>
              {main}
            </StatusText>
            <Spinner />
            <FandomMessage style={{ marginTop: '12px' }}>{messages[messageIndex]}</FandomMessage>
          </div>
        </MemorySummaryContainer>
      </Container>
    );
  }

  // Full loading view before memory loads
  return (
    <Container>
      <StatusContainer>
        <StatusText>{main}</StatusText>
        <SubText>{sub}</SubText>
        <FandomMessage>{messages[messageIndex]}</FandomMessage>
        
        {memoryContext && status === 'analyzing' && (
          <MemoryContext>{memoryContext}</MemoryContext>
        )}
        
        <Spinner />
      </StatusContainer>
    </Container>
  );
};

export default EnhancedLoadingStatus;